import type { AdminReportMediaDto } from '@carcheck/shared';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportVideo } from './ReportVideo';
import { labelReportMedia } from '../_lib/mediaLabels';

/**
 * Full-screen Lightbox for report media review (AUT-127). Mounts on
 * top of the moderation drawer via MUI `<Dialog fullScreen>` so all
 * other interactions blur — moderation is single-task by design.
 *
 * Behaviour (architect 🟠 #5):
 *   - `<img>` for photos, `<ReportVideo>` (plain HTML5 `<video>` with
 *     controls, poster and metadata preload) for videos — the admin
 *     watches walkaround clips here (AUT-272). Browser fallback string
 *     localised when `<video>` is unsupported.
 *   - Arrow-key navigation stands down while a media element has focus,
 *     so the operator's ←/→ scrubs the clip instead of jumping tiles.
 *   - Keyboard arrow keys cycle prev/next; ESC closes (MUI handles ESC,
 *     arrow handler is attached at window).
 *   - `onError` on the media element fires the parent's recovery
 *     callback — the parent invalidates the detail query so a fresh
 *     1h-TTL URL replaces the expired one (architect 🟠 #4).
 */
export interface LightboxProps {
  open: boolean;
  media: AdminReportMediaDto[];
  currentIndex: number;
  onClose: () => void;
  onIndexChange: (next: number) => void;
  /** Fired on `<img>` / `<video>` load error; parent invalidates detail. */
  onMediaError: () => void;
}

const NAV_BTN_SX = {
  color: '#fff',
  bgcolor: 'rgba(255,255,255,0.08)',
} as const;

/** Header overlay — close + index counter. */
const LightboxHeader = ({
  current,
  total,
  onClose,
}: {
  current: number;
  total: number;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
        <IconButton onClick={onClose} aria-label={t('reports.detail.close')} sx={NAV_BTN_SX}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 2 }}>
        <Typography sx={{ color: '#fff', fontFamily: 'monospace', fontSize: 14 }}>
          {`${current + 1} / ${total}`}
        </Typography>
      </Box>
    </>
  );
};

/** Prev/Next arrows — rendered only when there is more than one media item. */
const LightboxArrows = ({
  onPrev,
  onNext,
}: {
  onPrev: () => void;
  onNext: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <IconButton
        onClick={onPrev}
        aria-label={t('reports.mediaTab.prev')}
        sx={{
          ...NAV_BTN_SX,
          position: 'absolute',
          left: 16,
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        onClick={onNext}
        aria-label={t('reports.mediaTab.next')}
        sx={{
          ...NAV_BTN_SX,
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </>
  );
};

/**
 * Photo or video frame at the current index (documents render nothing).
 *
 * Videos go through `<ReportVideo>` (poster + controls + metadata
 * preload). It is keyed by media id so stepping from one clip to the
 * next REMOUNTS the element instead of swapping `src` underneath a
 * playing one — otherwise the previous clip's audio can outlive the
 * frame it belonged to.
 */
const LightboxFrame = ({
  item,
  label,
  onMediaError,
}: {
  item: AdminReportMediaDto;
  label: string;
  onMediaError: () => void;
}): ReactElement | null => {
  switch (item.mediaType) {
    case 'photo':
      return (
        <Box
          component="img"
          src={item.signedUrl}
          alt={label}
          onError={onMediaError}
          sx={{ maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain' }}
        />
      );
    case 'video':
      return (
        <ReportVideo
          key={item.id}
          media={item}
          label={label}
          onError={onMediaError}
          sx={{ maxWidth: '92vw', maxHeight: '90vh', backgroundColor: '#000' }}
        />
      );
    case 'document':
      // AUT-429 — PDFs open in a new tab from the tile; the caller never
      // pages over them. Defensive: render nothing rather than a broken <img>.
      return null;
    default: {
      const unreachable: never = item.mediaType;
      return unreachable;
    }
  }
};

export const Lightbox = ({
  open,
  media,
  currentIndex,
  onClose,
  onIndexChange,
  onMediaError,
}: LightboxProps): ReactElement | null => {
  const { t } = useTranslation();

  const goPrev = useCallback(() => {
    if (media.length === 0) return;
    onIndexChange((currentIndex - 1 + media.length) % media.length);
  }, [currentIndex, media.length, onIndexChange]);

  const goNext = useCallback(() => {
    if (media.length === 0) return;
    onIndexChange((currentIndex + 1) % media.length);
  }, [currentIndex, media.length, onIndexChange]);

  // Keyboard navigation — window-level so dialog doesn't need focus.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      // Future-proof: don't hijack keys while user types in a filter /
      // comment field that may render over the drawer.
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      // A focused <video> owns ←/→ for seeking. Stealing them to change
      // item would yank the clip away mid-scrub — the one interaction
      // this dialog exists for.
      if (e.target instanceof HTMLMediaElement) return;
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, goPrev, goNext]);

  const labeled = useMemo(() => labelReportMedia(media, t), [media, t]);
  const current = labeled[currentIndex];
  if (!current) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      slotProps={{ paper: { sx: { bgcolor: 'rgba(0,0,0,0.92)' } } }}
    >
      <LightboxHeader current={currentIndex} total={media.length} onClose={onClose} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          position: 'relative',
        }}
      >
        <LightboxFrame item={current.item} label={current.label} onMediaError={onMediaError} />
        {media.length > 1 ? <LightboxArrows onPrev={goPrev} onNext={goNext} /> : null}
      </Box>
    </Dialog>
  );
};
